/**
 * Reusable Google sync card — the ONE sync-setup control, embeddable from anywhere
 * (Calendar, Contacts, Tasks, Profile). Talks to System A (/api/google/*), the multi-tenant
 * per-user Google connection. Self-contained: fetches its own status, handles Connect / Sync /
 * Re-auth / Disconnect. Display logic is the pure, unit-tested googleSyncView module.
 *
 * Props:
 *   services  {string[]}  optional whitelist e.g. ['calendar'] — default shows Calendar+Contacts+Tasks
 *   onSynced  {function}  called with the sync response after a successful "Sync now" (e.g. reload)
 *   title     {string}    optional override for the header title
 */
import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../auth';
import { deriveGoogleView, visibleServices, syncSummary } from './googleSyncView';
import './GoogleSyncCard.css';

export default function GoogleSyncCard({ services, onSynced, title }) {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch('/api/google/status');
      setStatus(res.ok ? await res.json() : { connected: false, error: 'Unable to load Google status' });
    } catch (e) {
      setStatus({ connected: false, error: 'Unable to load Google status' });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = () => { window.location.href = '/auth/google/sync'; };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google sync? Your Calendar, Contacts and Tasks stay, but will be local-only.')) return;
    setMessage(null);
    try {
      const res = await authFetch('/api/google/disconnect', { method: 'POST' });
      if (!res.ok) throw new Error('Disconnect failed');
      setStatus({ connected: false });
      setMessage({ type: 'success', text: 'Google disconnected — now local-only.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Disconnect failed.' });
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const res = await authFetch('/api/google/sync', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Google sync failed');
      setMessage(syncSummary(data));
      await load();
      if (onSynced) onSynced(data);
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Google sync failed.' });
    } finally {
      setSyncing(false);
    }
  };

  const v = deriveGoogleView(status);
  const rows = visibleServices(services);
  const attention = v.needsAttention || message?.type === 'error';

  return (
    <div className={`gsync-card ${v.connected ? 'connected' : 'local-only'} ${attention ? 'attention' : ''}`} data-testid="google-sync-card">
      <div className="gsync-head">
        <div className="gsync-title">
          <span className="gsync-dot" aria-hidden="true" />
          <strong>{title || v.title}</strong>
        </div>
        <div className="gsync-actions">
          {v.connected ? (
            <>
              <button type="button" className="gsync-btn primary" onClick={syncNow} disabled={syncing} data-testid="gsync-sync">
                {syncing ? 'Syncing…' : 'Sync now'}
              </button>
              <button type="button" className="gsync-btn" onClick={connect} data-testid="gsync-reauth">Re-auth</button>
              <button type="button" className="gsync-btn danger" onClick={disconnect} data-testid="gsync-disconnect">Disconnect</button>
            </>
          ) : (
            <button type="button" className="gsync-btn primary" onClick={connect} data-testid="gsync-connect">Connect Google</button>
          )}
        </div>
      </div>

      <div className="gsync-detail">
        {v.connected
          ? <span>{v.email || 'Google account connected'}{v.lastSync ? ` · Last sync: ${new Date(v.lastSync).toLocaleString()}` : ''}</span>
          : <span>Connect Google to sync {rows.map((r) => r.label).join(', ')} — both ways.</span>}
      </div>

      <div className="gsync-services">
        {rows.map((r) => (
          <span key={r.key} className={`gsync-chip ${v.connected ? 'on' : 'off'}`}>
            <span className="gsync-chip-icon" aria-hidden="true">{r.icon}</span>{r.label}
          </span>
        ))}
      </div>

      {v.lastError && <div className="gsync-message error">{v.lastError}</div>}
      {message && <div className={`gsync-message ${message.type}`}>{message.text}</div>}
    </div>
  );
}
